/**
 * Who did what, and when.
 *
 * The log has been written all along -- every login, cancelled invoice,
 * correction, user change -- but could only be read on the server. This
 * is the read-only screen for it. There is no edit or delete, here or in
 * the API: a log an administrator can rewrite is not evidence.
 */
import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Button, Collapse, MenuItem, Skeleton, Stack, TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { fetchAudit, fetchAuditActions } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { c } from '../theme'

const PAGE = 50

/** "2026-10-18T13:32:05" (UTC from the server) as local time. */
export function localTime(iso: string): string {
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
    + `${p(d.getHours())}:${p(d.getMinutes())}`
}

export default function AuditLogPanel() {
  const { t } = useTranslation()
  const [action, setAction] = useState('')
  const [actor, setActor] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState<number | null>(null)

  const actions = useQuery({ queryKey: ['audit-actions'],
    queryFn: fetchAuditActions })
  const q = useQuery({
    queryKey: ['audit', action, actor, from, to, offset],
    queryFn: () => fetchAudit({
      action: action || undefined, actor: actor.trim() || undefined,
      from_date: from || undefined, to_date: to || undefined,
      limit: PAGE, offset }),
  })
  const reset = () => setOffset(0)

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ color: c.inkMuted }}>
        {t('audit.intro')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField select size="small" label={t('audit.action')} value={action}
          onChange={(e) => { setAction(e.target.value); reset() }}
          sx={{ minWidth: 260 }}>
          <MenuItem value="">{t('audit.allActions')}</MenuItem>
          {(actions.data ?? []).map((a) => (
            <MenuItem key={a} value={a}>{a}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label={t('audit.user')} value={actor}
          onChange={(e) => { setActor(e.target.value); reset() }} />
        <TextField size="small" type="date" label={t('report.from')} value={from}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => { setFrom(e.target.value); reset() }} />
        <TextField size="small" type="date" label={t('report.to')} value={to}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => { setTo(e.target.value); reset() }} />
      </Stack>

      {q.isLoading ? <><Skeleton /><Skeleton /><Skeleton /></> : q.isError ? (
        <Alert severity="error">{errorMessage(q.error)}</Alert>
      ) : (q.data?.entries ?? []).length === 0 ? (
        <Typography variant="body2" sx={{ color: c.inkMuted, py: 2 }}>
          {t('audit.none')}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table className="rpt" style={{ minWidth: 680 }}>
            <thead>
              <tr>
                <th style={{ width: 140 }}>{t('audit.when')}</th>
                <th style={{ width: 110 }}>{t('audit.user')}</th>
                <th style={{ width: 230 }}>{t('audit.action')}</th>
                <th>{t('audit.what')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.entries.map((e) => (
                <Fragment key={e.id}>
                  <tr className={e.detail ? 'clickable' : ''}
                    onClick={() => e.detail && setOpen(open === e.id ? null : e.id)}>
                    <td className="num" style={{ textAlign: 'start' }}>{localTime(e.at)}</td>
                    <td>{e.actor ?? '—'}</td>
                    <td className="num" style={{ textAlign: 'start',
                      fontSize: '0.78rem' }}>{e.action}</td>
                    <td>{e.summary ?? ''}</td>
                  </tr>
                  {e.detail && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0, border: 0 }}>
                        <Collapse in={open === e.id} unmountOnExit>
                          <Box component="pre" sx={{ m: 0, px: 2, py: 1,
                            bgcolor: c.surfacePage, fontSize: 11.5,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(e.detail, null, 2)}
                          </Box>
                        </Collapse>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Box>
      )}

      {q.data && q.data.total > PAGE && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            {t('audit.newer')}</Button>
          <Typography variant="caption" sx={{ color: c.inkMuted }}>
            {offset + 1}–{Math.min(offset + PAGE, q.data.total)} / {q.data.total}
          </Typography>
          <Button size="small" disabled={offset + PAGE >= q.data.total}
            onClick={() => setOffset(offset + PAGE)}>
            {t('audit.older')}</Button>
        </Stack>
      )}
    </Stack>
  )
}
